import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

const SECTEUR_COLOR  = { A: '#3b82f6', B: '#f59e0b' };
const PLANIF_EMAIL   = 'gestion-planif@valeatelecom.com';
const PERSONAL_EMAIL = 'samir.mrabet@valeatelecom.com';
const PRESET_COLORS  = [
  '#000000','#374151','#dc2626','#ea580c','#ca8a04',
  '#16a34a','#2563eb','#7c3aed','#db2777','#ffffff',
  '#fca5a5','#fed7aa','#fef08a','#bbf7d0','#bfdbfe',
];

// ── Helpers ────────────────────────────────────────────────────────────────────
function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso), now = new Date();
  if (d.toDateString() === now.toDateString())
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const yest = new Date(now); yest.setDate(yest.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return 'Hier';
  if (now - d < 7 * 86400000) return d.toLocaleDateString('fr-FR', { weekday: 'short' });
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function stripSelf(addresses, selfEmail) {
  if (!addresses) return '';
  return addresses.split(',').map(a => a.trim())
    .filter(a => !a.toLowerCase().includes(selfEmail)).join(', ');
}

function escHtml(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / 1048576).toFixed(1)} Mo`;
}
function getFileIcon(filename) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return '📄';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['xls','xlsx','csv'].includes(ext)) return '📊';
  if (['jpg','jpeg','png','gif','bmp','webp','svg'].includes(ext)) return '🖼';
  if (['zip','rar','7z','tar','gz'].includes(ext)) return '🗜';
  return '📎';
}
async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve({ filename: file.name, content: reader.result.split(',')[1], mimetype: file.type || 'application/octet-stream' });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function getJwtRole() {
  try {
    const token = localStorage.getItem('admin_token');
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1])).role;
  } catch { return null; }
}

// ── Signature storage ──────────────────────────────────────────────────────────
const SIG_DEFAULT = {
  label: 'Ma signature',
  name: '', title: '', company: 'VALEA TELECOM', phone: '', email: PLANIF_EMAIL,
  address: '13 Rue de la Parisienne\n69420 AMPUIS', website: 'www.valeatelecom.com',
  color: '#2d7a3a', logoBase64: '', logoWidth: 160,
  ecoMsg: "Before printing, think about the environment / Avant d'imprimer, pensez à l'environnement",
};

function getSigsStore() {
  try {
    const raw = localStorage.getItem('email_sigs_v3');
    if (raw) return JSON.parse(raw);
    const old = localStorage.getItem('email_sig_v2');
    if (old) {
      const sig = JSON.parse(old);
      const id = 'sig_' + Date.now();
      const store = { active: id, list: [{ ...SIG_DEFAULT, ...sig, id, label: sig.name || 'Principale' }] };
      localStorage.setItem('email_sigs_v3', JSON.stringify(store));
      return store;
    }
    return { active: null, list: [] };
  } catch { return { active: null, list: [] }; }
}

function saveSigsStore(store) { localStorage.setItem('email_sigs_v3', JSON.stringify(store)); }

// ── buildSigHtml ───────────────────────────────────────────────────────────────
function buildSigHtml(sig) {
  if (!sig) return '';
  const ecoColor = sig.color || '#2d7a3a';
  const F = 'font-family:Arial,sans-serif';
  const tds = [];
  if (sig.name)      tds.push(`<tr><td style="${F};padding:0 0 1px 0;line-height:18px;font-size:13px;font-weight:bold;color:#111">${sig.name}</td></tr>`);
  if (sig.title)     tds.push(`<tr><td style="${F};padding:0 0 4px 0;line-height:17px;font-size:12px;color:#444">${sig.title}</td></tr>`);
  if (sig.phone)     tds.push(`<tr><td style="${F};padding:0;line-height:17px;font-size:12px;color:#555">T&#233;l : ${sig.phone}</td></tr>`);
  if (sig.email)     tds.push(`<tr><td style="${F};padding:0;line-height:17px;font-size:12px;color:#555">&#64; : <a href="mailto:${sig.email}" style="color:#1a56db;text-decoration:none">${sig.email}</a></td></tr>`);
  if (sig.logoBase64) tds.push(`<tr><td style="padding:10px 0 6px 0"><img src="${sig.logoBase64}" width="${sig.logoWidth || 160}" height="auto" style="display:block;border:0;max-width:${sig.logoWidth || 160}px"></td></tr>`);
  if (sig.company)   tds.push(`<tr><td style="${F};padding:2px 0 0 0;line-height:17px;font-size:12px;font-weight:bold;color:#111">${sig.company}</td></tr>`);
  if (sig.address)   sig.address.split('\n').forEach(l => { if (l.trim()) tds.push(`<tr><td style="${F};padding:0;line-height:17px;font-size:12px;color:#555">${l.trim()}</td></tr>`); });
  if (sig.website)   tds.push(`<tr><td style="${F};padding:0;line-height:17px;font-size:12px"><a href="https://${sig.website.replace(/^https?:\/\//, '')}" style="color:#555;text-decoration:none">${sig.website}</a></td></tr>`);
  const eco = sig.ecoMsg !== undefined ? sig.ecoMsg : "Before printing, think about the environment / Avant d'imprimer, pensez à l'environnement";
  if (eco) tds.push(`<tr><td style="${F};padding:8px 0 0 0;line-height:16px;font-size:11px;font-style:italic;color:${ecoColor}">${eco}</td></tr>`);
  return `<table cellpadding="0" cellspacing="0" border="0" style="margin-top:24px;border-collapse:collapse"><tr><td style="border-top:1px solid #dddddd;padding-top:10px"><table cellpadding="0" cellspacing="0" border="0">${tds.join('')}</table></td></tr></table>`;
}

function buildHtmlBodyRich(htmlContent, sig) {
  return `<div style="font-family:Arial,sans-serif;font-size:13px;color:#111;line-height:1.6">${htmlContent || ''}${buildSigHtml(sig)}</div>`;
}

// ── Rich text components ───────────────────────────────────────────────────────
function ToolBtn({ children, onClick, title, active }) {
  return (
    <button onMouseDown={e => { e.preventDefault(); onClick?.(); }} title={title}
      style={{ minWidth: 27, height: 26, padding: '0 4px', borderRadius: 4, border: `1px solid ${active ? 'var(--accent)' : 'transparent'}`, background: active ? 'color-mix(in srgb,var(--accent) 15%,var(--bg))' : 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, lineHeight: 1 }}>
      {children}
    </button>
  );
}

function Sep() {
  return <div style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 3px', flexShrink: 0 }} />;
}

function ColorPicker({ onSelect }) {
  return (
    <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 99, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 8, boxShadow: '0 4px 16px rgba(0,0,0,.25)', display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 4, width: 134, marginTop: 3 }}>
      {PRESET_COLORS.map(c => (
        <button key={c} onMouseDown={e => { e.preventDefault(); onSelect(c); }}
          style={{ width: 20, height: 20, borderRadius: 3, background: c, border: c === '#ffffff' ? '1px solid #d1d5db' : '1px solid transparent', cursor: 'pointer', padding: 0, display: 'block' }} />
      ))}
      <input type="color" defaultValue="#000000" title="Couleur personnalisée…"
        onInput={e => onSelect(e.target.value)}
        style={{ width: 20, height: 20, padding: 0, border: '1px solid var(--border)', cursor: 'pointer', borderRadius: 3 }} />
    </div>
  );
}

function RichToolbar({ editorRef }) {
  const [fgOpen, setFgOpen] = useState(false);
  const [bgOpen, setBgOpen] = useState(false);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  return (
    <div style={{ display: 'flex', gap: 1, padding: '4px 10px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', alignItems: 'center', background: 'color-mix(in srgb,var(--bg2) 60%,var(--bg))', flexShrink: 0 }}>
      <ToolBtn onClick={() => exec('bold')}          title="Gras (Ctrl+B)"><strong style={{ fontFamily: 'serif' }}>G</strong></ToolBtn>
      <ToolBtn onClick={() => exec('italic')}        title="Italique (Ctrl+I)"><em style={{ fontFamily: 'serif' }}>I</em></ToolBtn>
      <ToolBtn onClick={() => exec('underline')}     title="Souligné (Ctrl+U)"><span style={{ textDecoration: 'underline' }}>S</span></ToolBtn>
      <ToolBtn onClick={() => exec('strikeThrough')} title="Barré"><span style={{ textDecoration: 'line-through' }}>S</span></ToolBtn>
      <Sep />
      <div style={{ position: 'relative' }}>
        <ToolBtn onClick={() => { setBgOpen(false); setFgOpen(v => !v); }} title="Couleur du texte" active={fgOpen}>
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
            <span style={{ fontWeight: 700, fontSize: 12 }}>A</span>
            <span style={{ display: 'block', width: 14, height: 2.5, background: '#dc2626', borderRadius: 1, marginTop: 1 }} />
          </span>
        </ToolBtn>
        {fgOpen && <ColorPicker onSelect={c => { exec('foreColor', c); setFgOpen(false); }} />}
      </div>
      <div style={{ position: 'relative' }}>
        <ToolBtn onClick={() => { setFgOpen(false); setBgOpen(v => !v); }} title="Surligné / couleur de fond" active={bgOpen}>
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
            <span style={{ fontSize: 11 }}>ab</span>
            <span style={{ display: 'block', width: 14, height: 2.5, background: '#fde047', borderRadius: 1, marginTop: 1 }} />
          </span>
        </ToolBtn>
        {bgOpen && <ColorPicker onSelect={c => { exec('hiliteColor', c) || exec('backColor', c); setBgOpen(false); }} />}
      </div>
      <Sep />
      <ToolBtn onClick={() => exec('insertUnorderedList')} title="Liste à puces">
        <span style={{ fontSize: 10 }}>• —</span>
      </ToolBtn>
      <ToolBtn onClick={() => exec('insertOrderedList')} title="Liste numérotée">
        <span style={{ fontSize: 10 }}>1.</span>
      </ToolBtn>
      <Sep />
      <ToolBtn onClick={() => exec('justifyLeft')}   title="Aligner à gauche"><span style={{ fontSize: 11 }}>⬅</span></ToolBtn>
      <ToolBtn onClick={() => exec('justifyCenter')} title="Centrer"><span style={{ fontSize: 11 }}>↔</span></ToolBtn>
      <ToolBtn onClick={() => exec('justifyRight')}  title="Aligner à droite"><span style={{ fontSize: 11 }}>➡</span></ToolBtn>
      <Sep />
      <ToolBtn onClick={() => exec('removeFormat')} title="Effacer la mise en forme">
        <span style={{ fontSize: 11, position: 'relative' }}>T<span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#ef4444', fontWeight: 300, lineHeight: 1 }}>×</span></span>
      </ToolBtn>
    </div>
  );
}

function RichEditor({ editorRef, placeholder, onInput, minHeight = 180 }) {
  const [empty, setEmpty] = useState(true);

  const handleInput = (e) => {
    const isEmpty = !e.currentTarget.textContent.trim();
    setEmpty(isEmpty);
    onInput?.(e, !isEmpty);
  };

  return (
    <div style={{ position: 'relative' }}>
      {empty && (
        <div style={{ position: 'absolute', top: 10, left: 14, color: 'var(--text3)', fontSize: 13, lineHeight: 1.6, pointerEvents: 'none', userSelect: 'none', fontFamily: 'Arial,sans-serif' }}>
          {placeholder}
        </div>
      )}
      <div ref={editorRef} contentEditable suppressContentEditableWarning onInput={handleInput}
        style={{ minHeight, outline: 'none', padding: '10px 14px', color: 'var(--text)', fontSize: 13, lineHeight: 1.6, fontFamily: 'Arial,sans-serif', wordBreak: 'break-word' }} />
    </div>
  );
}

// ── SigInp — module-level pour éviter le remontage à chaque frappe ─────────────
function SigInp({ label, value, onChange, type }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</label>
      {type === 'color'
        ? <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="color" value={value || '#2d7a3a'} onChange={e => onChange(e.target.value)} style={{ width: 36, height: 28, border: 'none', cursor: 'pointer', borderRadius: 4 }} />
            <span style={{ fontSize: 12, color: 'var(--text2)' }}>{value}</span>
          </div>
        : <input type={type || 'text'} value={value || ''} onChange={e => onChange(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, boxSizing: 'border-box' }} />
      }
    </div>
  );
}

// ── SigEditModal ───────────────────────────────────────────────────────────────
function SigEditModal({ initial, onSave, onClose }) {
  const [sig, setSig]     = useState(() => ({ ...SIG_DEFAULT, ...initial }));
  const [preview, setPreview] = useState(false);
  const previewRef  = useRef();
  const fileInputRef = useRef();

  useEffect(() => {
    if (preview && previewRef.current) {
      const html = buildSigHtml(sig);
      const doc  = previewRef.current.contentDocument;
      doc.open(); doc.write(`<!DOCTYPE html><html><head><style>body{margin:12px;background:#fff;font-family:Arial,sans-serif}</style></head><body>${html}</body></html>`); doc.close();
    }
  }, [preview, sig]);

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = ev => setSig(s => ({ ...s, logoBase64: ev.target.result }));
    reader.onerror = () => alert('Impossible de lire ce fichier image.');
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const up = (field) => (v) => setSig(s => ({ ...s, [field]: v }));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, padding: '24px 28px', width: 540, maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', margin: 0 }}>{initial.id ? '✏ Modifier' : '+ Nouvelle'} signature</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text3)' }}>✕</button>
        </div>
        <SigInp label="Nom de la signature" value={sig.label} onChange={up('label')} />
        <p style={sectionLabel}>Informations personnelles</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <SigInp label="Nom complet"    value={sig.name}  onChange={up('name')} />
          <SigInp label="Titre / Fonction" value={sig.title} onChange={up('title')} />
          <SigInp label="Téléphone"      value={sig.phone} onChange={up('phone')} />
          <SigInp label="Email affiché"  value={sig.email} onChange={up('email')} />
        </div>
        <p style={sectionLabel}>Logo</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
          <button onClick={() => fileInputRef.current?.click()}
            style={{ padding: '6px 14px', borderRadius: 7, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
            Choisir une image…
          </button>
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp" onChange={handleLogo} style={{ display: 'none' }} />
          {sig.logoBase64 && (
            <>
              <img src={sig.logoBase64} alt="logo" style={{ maxWidth: 140, maxHeight: 60, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 6, padding: 4, background: '#fff' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, color: 'var(--text3)' }}>Largeur (px)</label>
                <input type="number" value={sig.logoWidth || 160} min="40" max="400"
                  onChange={e => setSig(s => ({ ...s, logoWidth: parseInt(e.target.value) || 160 }))}
                  style={{ width: 70, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 12 }} />
              </div>
              <button onClick={() => setSig(s => ({ ...s, logoBase64: '' }))}
                style={{ fontSize: 11, color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer' }}>✕ retirer</button>
            </>
          )}
        </div>
        <p style={sectionLabel}>Société</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <SigInp label="Nom société" value={sig.company} onChange={up('company')} />
          <SigInp label="Site web"    value={sig.website} onChange={up('website')} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.05em' }}>Adresse</label>
          <textarea value={sig.address || ''} onChange={e => setSig(s => ({ ...s, address: e.target.value }))} rows={2}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, boxSizing: 'border-box', resize: 'none', fontFamily: 'inherit' }} />
        </div>
        <p style={sectionLabel}>Message écologique (ligne verte en bas)</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0 12px', alignItems: 'end', marginBottom: 12 }}>
          <SigInp label="Texte" value={sig.ecoMsg} onChange={up('ecoMsg')} />
          <SigInp label="Couleur" type="color" value={sig.color || '#2d7a3a'} onChange={up('color')} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <button onClick={() => setPreview(v => !v)} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', cursor: 'pointer' }}>
            {preview ? '▲ Masquer aperçu' : '▼ Aperçu'}
          </button>
          {preview && <iframe ref={previewRef} title="sig-preview" style={{ width: '100%', height: 240, border: '1px solid var(--border)', borderRadius: 7, marginTop: 8, background: '#fff' }} sandbox="allow-same-origin" />}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnS('var(--bg3)', 'var(--text2)')}>Annuler</button>
          <button onClick={() => onSave(sig)} style={btnS('var(--accent)', '#fff')}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

// ── SigManagerModal ────────────────────────────────────────────────────────────
function SigManagerModal({ onClose }) {
  const [store, setStore] = useState(getSigsStore);
  const [editing, setEditing] = useState(null);
  const updateStore = (s) => { saveSigsStore(s); setStore({ ...s }); };
  const handleSave = (sig) => {
    const isNew = !store.list.find(s => s.id === sig.id);
    const list  = isNew ? [...store.list, sig] : store.list.map(s => s.id === sig.id ? sig : s);
    updateStore({ active: store.active || sig.id, list });
    setEditing(null);
  };
  const deleteSig = (id) => {
    if (!window.confirm('Supprimer cette signature ?')) return;
    const list = store.list.filter(s => s.id !== id);
    updateStore({ active: store.active === id ? (list[0]?.id || null) : store.active, list });
  };
  if (editing !== null) return <SigEditModal initial={editing} onSave={handleSave} onClose={() => setEditing(null)} />;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, padding: '24px 28px', width: 480, maxWidth: '96vw', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', margin: 0 }}>Mes signatures</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text3)' }}>✕</button>
        </div>
        {store.list.length === 0 && <p style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', margin: '20px 0' }}>Aucune signature. Créez-en une !</p>}
        {store.list.map(sig => (
          <div key={sig.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 9, border: `1.5px solid ${store.active === sig.id ? 'var(--accent)' : 'var(--border)'}`, background: store.active === sig.id ? 'color-mix(in srgb,var(--accent) 6%,var(--bg))' : 'var(--bg2)', marginBottom: 8 }}>
            <input type="radio" checked={store.active === sig.id} onChange={() => updateStore({ ...store, active: sig.id })} style={{ cursor: 'pointer', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sig.label || sig.name || 'Sans nom'}</div>
              {(sig.name || sig.title) && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{[sig.name, sig.title].filter(Boolean).join(' — ')}</div>}
            </div>
            {store.active === sig.id && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 6, padding: '1px 6px', flexShrink: 0 }}>Active</span>}
            <button onClick={() => setEditing(sig)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', cursor: 'pointer', flexShrink: 0 }}>Modifier</button>
            <button onClick={() => deleteSig(sig.id)} style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid #fca5a5', background: 'transparent', color: '#ef4444', cursor: 'pointer', flexShrink: 0 }}>✕</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={() => setEditing({ id: 'sig_' + Date.now(), ...SIG_DEFAULT })}
            style={{ padding: '7px 16px', borderRadius: 8, border: '1.5px dashed var(--accent)', background: 'transparent', color: 'var(--accent)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            + Nouvelle signature
          </button>
          <button onClick={onClose} style={btnS('var(--bg3)', 'var(--text2)')}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

// ── EmailItem ──────────────────────────────────────────────────────────────────
function EmailItem({ email, selected, onClick, onSecteur, sentView, showSecteur, isCC, onMarkRead }) {
  const [hovered, setHovered] = useState(false);
  const barColor    = email.secteur ? SECTEUR_COLOR[email.secteur] : 'var(--border)';
  const displayName = sentView
    ? (email.to?.replace(/<.*>/, '').trim() || email.to || '—')
    : (email.from?.replace(/<.*>/, '').trim() || email.from || '—');
  const isUnread = !email.read && !sentView;
  const bg = selected
    ? 'color-mix(in srgb,var(--accent) 10%,var(--bg))'
    : isUnread ? 'color-mix(in srgb,#3b82f6 6%,var(--bg2))' : 'var(--bg)';

  return (
    <div onClick={onClick}
      onDoubleClick={() => { if (!sentView && !email.read) onMarkRead?.(email.uid, true); }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ padding: '9px 10px 9px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: bg, borderLeft: `3px solid ${(sentView || !showSecteur) ? 'var(--border)' : barColor}`, transition: 'background .12s' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
        <span style={{ fontSize: 12, fontWeight: isUnread ? 700 : 500, color: 'var(--text)', maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {sentView && <span style={{ fontSize: 9, color: 'var(--text3)', marginRight: 4 }}>À :</span>}
          {displayName}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          {isUnread && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#3b82f6', display: 'inline-block', flexShrink: 0 }} />}
          {email.hasAttachments && <span style={{ fontSize: 11, opacity: 0.55, lineHeight: 1 }} title="Pièce jointe">📎</span>}
          <span style={{ fontSize: 10, color: isUnread ? 'var(--text2)' : 'var(--text3)' }}>{formatDate(email.date)}</span>
        </div>
      </div>
      <div style={{ fontSize: 11, fontWeight: isUnread ? 600 : 500, color: isUnread ? 'var(--text)' : 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 }}>
        {email.subject}
      </div>
      {!sentView && (
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', minHeight: 16 }}>
          {isCC && <span style={{ fontSize: 9, fontWeight: 700, background: '#f59e0b22', color: '#d97706', border: '1px solid #f59e0b66', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>Copie</span>}
          {showSecteur && email.secteur && <span style={{ fontSize: 9, fontWeight: 700, background: SECTEUR_COLOR[email.secteur], color: '#fff', borderRadius: 10, padding: '1px 7px' }}>Sec. {email.secteur}</span>}
          {showSecteur && hovered && (
            <div style={{ display: 'flex', gap: 3 }} onClick={e => e.stopPropagation()}>
              {!email.secteur && ['A', 'B'].map(s => (
                <button key={s} onClick={() => onSecteur(email.uid, s)}
                  style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, border: `1.5px solid ${SECTEUR_COLOR[s]}`, background: 'transparent', color: SECTEUR_COLOR[s], cursor: 'pointer', fontWeight: 700 }}>{s}</button>
              ))}
              {email.secteur && <button onClick={() => onSecteur(email.uid, null)}
                style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text3)', cursor: 'pointer' }}>✕ retirer</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── ComposeField ───────────────────────────────────────────────────────────────
function ComposeField({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)', padding: '4px 0' }}>
      <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 700, width: 44, flexShrink: 0, textAlign: 'right', paddingRight: 8 }}>{label}</span>
      <input value={value} onChange={e => onChange(e.target.value)}
        style={{ flex: 1, border: 'none', background: 'transparent', color: 'var(--text)', fontSize: 12, outline: 'none', padding: '2px 4px' }} />
    </div>
  );
}

// ── SigSelector ────────────────────────────────────────────────────────────────
function SigSelector({ store, sigId, setSigId, onManage }) {
  if (!store.list?.length) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0', gap: 8 }}>
        <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 700, width: 44, textAlign: 'right', paddingRight: 8, flexShrink: 0 }}>Sig.</span>
        <button onClick={onManage} style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Créer une signature</button>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0', gap: 6 }}>
      <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 700, width: 44, textAlign: 'right', paddingRight: 8, flexShrink: 0 }}>Sig.</span>
      <select value={sigId} onChange={e => setSigId(e.target.value)}
        style={{ flex: 1, fontSize: 12, border: 'none', background: 'transparent', color: 'var(--text)', outline: 'none', cursor: 'pointer' }}>
        <option value="">— Aucune —</option>
        {store.list.map(s => <option key={s.id} value={s.id}>{s.label || s.name || 'Sans nom'}</option>)}
      </select>
      <button onClick={onManage} style={{ fontSize: 11, color: 'var(--text3)', background: 'none', border: '1px solid var(--border)', borderRadius: 5, cursor: 'pointer', padding: '2px 7px' }}>Gérer</button>
    </div>
  );
}

// ── AttachmentList — affichage des PJ reçues ───────────────────────────────────
function AttachmentList({ attachments, mbBase, uid, sentView }) {
  if (!attachments?.length) return null;
  const base = sentView ? `${mbBase}/sent/attachment/${uid}` : `${mbBase}/attachment/${uid}`;

  const handleDownload = async (att) => {
    try {
      const res = await api.get(`${base}/${att.safeName}`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch {
      alert('Erreur lors du téléchargement de la pièce jointe.');
    }
  };

  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: '8px 16px 10px', background: 'var(--bg2)', flexShrink: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
        📎 {attachments.length} pièce{attachments.length > 1 ? 's' : ''} jointe{attachments.length > 1 ? 's' : ''}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {attachments.map((att, i) => (
          <div key={i}
            role="button"
            onClick={() => handleDownload(att)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px 4px 8px',
              background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 20,
              fontSize: 11, color: 'var(--text2)', cursor: 'pointer',
              maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              transition: 'border-color .15s, background .15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'color-mix(in srgb,var(--accent) 8%,var(--bg))'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg)'; }}>
            <span style={{ fontSize: 14, flexShrink: 0 }}>{getFileIcon(att.filename)}</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{att.filename}</span>
            {att.size > 0 && <span style={{ color: 'var(--text3)', fontSize: 10, flexShrink: 0 }}>{formatSize(att.size)}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── AttachmentPicker — sélection de PJ pour envoi ──────────────────────────────
function AttachmentPicker({ files, onChange }) {
  const inputRef = useRef(null);
  const handleFiles = async (e) => {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;
    const converted = await Promise.all(selected.map(fileToBase64));
    onChange(prev => [...prev, ...converted]);
    e.target.value = '';
  };
  const remove = (i) => onChange(prev => prev.filter((_, idx) => idx !== i));
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      {files.map((f, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px 2px 8px',
          background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, fontSize: 11, color: 'var(--text2)' }}>
          <span style={{ fontSize: 13 }}>{getFileIcon(f.filename)}</span>
          <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.filename}</span>
          <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: '0 0 0 2px', lineHeight: 1, fontSize: 14 }}>×</button>
        </span>
      ))}
      <button onClick={() => inputRef.current?.click()}
        style={{ padding: '3px 10px', borderRadius: 12, border: '1px dashed var(--border)',
          background: 'transparent', color: 'var(--text3)', fontSize: 11, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 4, transition: 'border-color .15s, color .15s' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text3)'; }}>
        📎 Joindre
        <input ref={inputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFiles} />
      </button>
    </div>
  );
}

// ── ComposeModal ───────────────────────────────────────────────────────────────
function ComposeModal({ onClose, mbBase }) {
  const [to, setTo]   = useState('');
  const [cc, setCc]   = useState('');
  const [subj, setSubj] = useState('');
  const editorRef     = useRef(null);
  const [editorHasContent, setEditorHasContent] = useState(false);
  const [store, setStore] = useState(getSigsStore);
  const [sigId, setSigId] = useState(() => getSigsStore().active || '');
  const [sending, setSending]       = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [sigManager, setSigManager] = useState(false);
  const [attachFiles, setAttachFiles] = useState([]);

  const selectedSig = store.list?.find(s => s.id === sigId) || null;

  const handleCorrect = async () => {
    const text = editorRef.current?.innerText?.trim() || '';
    if (!text) return;
    setCorrecting(true);
    try {
      const r = await api.post('/admin/emails/ai-correct', { text });
      const html = r.data.corrected.split('\n').map(l =>
        l.trim() ? `<p style="margin:0 0 4px 0">${escHtml(l)}</p>` : '<p style="margin:0;height:6px"></p>'
      ).join('');
      if (editorRef.current) { editorRef.current.innerHTML = html; setEditorHasContent(true); }
    } catch (e) { alert('Erreur IA : ' + (e.response?.data?.error || e.message)); }
    finally { setCorrecting(false); }
  };

  const handleSend = async () => {
    const htmlContent = editorRef.current?.innerHTML || '';
    const textContent = editorRef.current?.innerText?.trim() || '';
    if (!to || !subj || !textContent) return;
    setSending(true);
    try {
      await api.post(`${mbBase}/send`, {
        to, cc: cc || undefined, subject: subj,
        body: textContent,
        htmlBody: buildHtmlBodyRich(htmlContent, selectedSig),
        ...(attachFiles.length ? { attachments: attachFiles } : {}),
      });
      onClose();
    } catch (e) { alert('Erreur envoi : ' + (e.response?.data?.error || e.message)); }
    finally { setSending(false); }
  };

  const closeSigManager = () => { setSigManager(false); setStore(getSigsStore()); setSigId(getSigsStore().active || ''); };

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 900 }}>
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, width: 680, maxWidth: '96vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
          <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--text)' }}>✉ Nouveau message</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text3)' }}>✕</button>
          </div>
          <div style={{ padding: '0 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <ComposeField label="À"     value={to}   onChange={setTo} />
            <ComposeField label="Cc"    value={cc}   onChange={setCc} />
            <ComposeField label="Objet" value={subj} onChange={setSubj} />
            <SigSelector store={store} sigId={sigId} setSigId={setSigId} onManage={() => setSigManager(true)} />
          </div>
          <RichToolbar editorRef={editorRef} />
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <RichEditor editorRef={editorRef} placeholder="Écrivez votre message…"
              onInput={(_, hasContent) => setEditorHasContent(hasContent)} minHeight={220} />
          </div>
          <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
            {attachFiles.length > 0 && (
              <div style={{ paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
                <AttachmentPicker files={attachFiles} onChange={setAttachFiles} />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Btn onClick={handleSend} disabled={sending || !to || !subj || !editorHasContent} accent>{sending ? 'Envoi…' : 'Envoyer'}</Btn>
              <Btn onClick={handleCorrect} disabled={correcting || !editorHasContent}>{correcting ? 'Correction…' : '✦ Corriger orthographe'}</Btn>
              <Btn onClick={onClose} ghost>Annuler</Btn>
              <div style={{ marginLeft: 'auto' }}>
                <AttachmentPicker files={attachFiles} onChange={setAttachFiles} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {sigManager && <SigManagerModal onClose={closeSigManager} />}
    </>
  );
}

// ── EmailDetail ────────────────────────────────────────────────────────────────
function EmailDetail({ uid, sentView, mbBase, selfEmail, emailRead, onMarkRead }) {
  const [body, setBody]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [mode, setMode]         = useState(null);
  const [compTo, setCompTo]     = useState('');
  const [compCc, setCompCc]     = useState('');
  const [compSubj, setCompSubj] = useState('');
  const compEditorRef           = useRef(null);
  const pendingContent          = useRef('');
  const [editorHasContent, setEditorHasContent] = useState(false);
  const [store, setStore]       = useState(getSigsStore);
  const [sigId, setSigId]       = useState(() => getSigsStore().active || '');
  const [aiLoading, setAiLoading]   = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [sending, setSending]   = useState(false);
  const [quotedHtml, setQuotedHtml] = useState('');
  const [sent, setSent]         = useState(false);
  const [sigManager, setSigManager] = useState(false);
  const [attachFiles, setAttachFiles] = useState([]);
  const iframeRef = useRef();

  useEffect(() => {
    setLoading(true); setMode(null); setSent(false);
    const url = sentView ? `${mbBase}/sent/${uid}/body` : `${mbBase}/${uid}/body`;
    api.get(url).then(r => { setBody(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [uid, sentView, mbBase]);

  useEffect(() => {
    if (body?.html && iframeRef.current) {
      const doc  = iframeRef.current.contentDocument;
      const html = `<!DOCTYPE html><html><head><style>html,body{background:#ffffff!important;margin:0;padding:0}body{font-family:Arial,sans-serif;font-size:13px;padding:16px;color:#111;word-wrap:break-word}*{max-width:100%!important}img{max-width:100%!important;height:auto}a{color:#1a56db}</style></head><body>${body.html}</body></html>`;
      doc.open(); doc.write(html); doc.close();
    }
  }, [body]);

  // Set editor content after mode mounts
  useEffect(() => {
    if (mode && compEditorRef.current) {
      compEditorRef.current.innerHTML = pendingContent.current;
      const hasContent = !!compEditorRef.current.textContent.trim();
      setEditorHasContent(hasContent);
    }
  }, [mode]);

  const textToHtml = (text) => text.split('\n').map(l =>
    l.trim() ? `<p style="margin:0 0 2px 0;font-size:13px">${escHtml(l)}</p>` : '<p style="margin:0;height:6px"></p>'
  ).join('');

  const openMode = (m) => {
    if (!body) return;
    if (m === 'reply' || m === 'replyAll') {
      setCompTo(body.replyTo || body.from || '');
      setCompCc(m === 'replyAll' ? stripSelf([body.to, body.cc].filter(Boolean).join(', '), selfEmail) : '');
      setCompSubj(body.subject?.startsWith('Re:') ? body.subject : `Re: ${body.subject}`);
      const dateFr = body.date ? new Date(body.date).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }) : '';
      const originalHtml = body.html || textToHtml(body.text || '');
      const quoteBlock = `<div style="border-left:3px solid #ccc;padding-left:12px;margin:8px 0;color:#555;font-size:12px"><p style="margin:0 0 6px 0"><strong>De :</strong> ${escHtml(body.from || '')} &nbsp;|&nbsp; <strong>Envoy\u00e9 le :</strong> ${escHtml(dateFr)} &nbsp;|&nbsp; <strong>\u00c0 :</strong> ${escHtml(body.to || '')} &nbsp;|&nbsp; <strong>Objet :</strong> ${escHtml(body.subject || '')}</p><div>${originalHtml}</div></div>`;
      pendingContent.current = '';
      setQuotedHtml(quoteBlock);
    } else {
      setCompTo(''); setCompCc('');
      setCompSubj(`Tr: ${body.subject}`);
      const dateFr = body.date ? new Date(body.date).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }) : '';
      const lines = ['', '', '---------- Message transféré ----------',
        `De : ${body.from}`, `Date : ${dateFr}`, `Objet : ${body.subject}`, `À : ${body.to}`, '',
        ...(body.text || '').split('\n')];
      pendingContent.current = textToHtml(lines.join('\n'));
    }
    if (!m) setQuotedHtml('');
    setMode(m); setSent(false); setAttachFiles([]);
  };

  const setEditorHtml = (html) => {
    if (compEditorRef.current) {
      compEditorRef.current.innerHTML = html;
      setEditorHasContent(!!compEditorRef.current.textContent.trim());
    }
  };

  const handleAI = async () => {
    if (!body) return;
    setAiLoading(true);
    try {
      const r = await api.post('/admin/emails/ai-reply', { originalText: body.text || body.subject, subject: body.subject, from: body.from });
      const html = r.data.reply.split('\n').map(l =>
        l.trim() ? `<p style="margin:0 0 4px 0">${escHtml(l)}</p>` : '<p style="margin:0;height:6px"></p>'
      ).join('');
      if (mode && compEditorRef.current) {
        setEditorHtml(html);
      } else {
        pendingContent.current = html;
        setCompTo(body.replyTo || body.from || '');
        setCompCc('');
        setCompSubj(body.subject?.startsWith('Re:') ? body.subject : `Re: ${body.subject}`);
        setMode('reply');
      }
    } catch (e) { alert('Erreur IA : ' + (e.response?.data?.error || e.message)); }
    finally { setAiLoading(false); }
  };

  const handleCorrect = async () => {
    const text = compEditorRef.current?.innerText?.trim() || '';
    if (!text) return;
    setCorrecting(true);
    try {
      const r = await api.post('/admin/emails/ai-correct', { text });
      const html = r.data.corrected.split('\n').map(l =>
        l.trim() ? `<p style="margin:0 0 4px 0">${escHtml(l)}</p>` : '<p style="margin:0;height:6px"></p>'
      ).join('');
      setEditorHtml(html);
    } catch (e) { alert('Erreur IA : ' + (e.response?.data?.error || e.message)); }
    finally { setCorrecting(false); }
  };

  const handleSend = async () => {
    const htmlContent = compEditorRef.current?.innerHTML || '';
    const textContent = compEditorRef.current?.innerText?.trim() || '';
    if (!compTo || !textContent) return;
    setSending(true);
    try {
      const selectedSig = store.list?.find(s => s.id === sigId) || null;
      // Signature placée AVANT la citation pour éviter qu'elle soit masquée par Gmail/Outlook
      const sigHtml = buildSigHtml(selectedSig);
      const fullHtml = htmlContent + sigHtml + (quotedHtml ? `<br>${quotedHtml}` : '');
      await api.post(`${mbBase}/send`, {
        to: compTo, cc: compCc || undefined, subject: compSubj,
        body: textContent,
        htmlBody: buildHtmlBodyRich(fullHtml, null),
        inReplyTo: (mode === 'reply' || mode === 'replyAll') ? body?.messageId : undefined,
        ...(attachFiles.length ? { attachments: attachFiles } : {}),
      });
      setSent(true); setMode(null);
      if (!sentView) onMarkRead?.(uid, true);
    } catch (e) { alert('Erreur envoi : ' + (e.response?.data?.error || e.message)); }
    finally { setSending(false); }
  };

  const closeSigManager = () => { setSigManager(false); setStore(getSigsStore()); setSigId(getSigsStore().active || ''); };

  if (loading) return <Placeholder>Chargement…</Placeholder>;
  if (!body)   return <Placeholder>Impossible de charger ce message.</Placeholder>;

  const hasOtherRecipients = !sentView && !!(body.cc || stripSelf(body.to, selfEmail));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'var(--bg)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
          {sentView && <span style={{ fontSize: 10, fontWeight: 700, background: '#22c55e22', color: '#16a34a', border: '1px solid #16a34a44', borderRadius: 5, padding: '2px 7px', flexShrink: 0, marginTop: 2 }}>Envoyé</span>}
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{body.subject}</span>
        </div>
        {[['De', body.from], ['À', body.to], ['Cc', body.cc], ['Date', body.date ? new Date(body.date).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }) : '']].filter(([, v]) => v).map(([l, v]) => (
          <div key={l} style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 1 }}>
            <span style={{ color: 'var(--text3)', fontWeight: 600 }}>{l} : </span>{v}
          </div>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', background: '#ffffff', minHeight: 0 }}>
        {body.html
          ? <iframe ref={iframeRef} title="email-body" sandbox="allow-same-origin" style={{ width: '100%', height: '100%', border: 'none', minHeight: 300, display: 'block' }} />
          : <div style={{ padding: '16px 20px', fontSize: 13, color: '#111', whiteSpace: 'pre-wrap', lineHeight: 1.6, background: '#fff' }}>{body.text}</div>
        }
      </div>

      <AttachmentList attachments={body.attachments} mbBase={mbBase} uid={uid} sentView={sentView} />

      <div style={{ borderTop: '1px solid var(--border)', flexShrink: 0, background: 'var(--bg)' }}>
        {sent && <div style={{ padding: '8px 16px', color: '#22c55e', fontSize: 12, fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Message envoyé.</div>}
        {!mode ? (
          <div style={{ display: 'flex', gap: 6, padding: '10px 14px', flexWrap: 'wrap' }}>
            {!sentView && <Btn onClick={() => openMode('reply')}>Répondre</Btn>}
            {hasOtherRecipients && <Btn onClick={() => openMode('replyAll')}>Répondre à tous</Btn>}
            <Btn onClick={() => openMode('forward')}>Transférer</Btn>
            {!sentView && <Btn onClick={handleAI} disabled={aiLoading} accent>{aiLoading ? 'Génération…' : '✦ Réponse IA'}</Btn>}
            {!sentView && emailRead && (
              <Btn onClick={() => onMarkRead?.(uid, false)} ghost>◎ Marquer non lu</Btn>
            )}
            {!sentView && !emailRead && (
              <Btn onClick={() => onMarkRead?.(uid, true)} ghost>✓ Marquer lu</Btn>
            )}
            <Btn onClick={() => setSigManager(true)} ghost>✏ Signatures</Btn>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '55vh' }}>
            <div style={{ padding: '0 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <ComposeField label="À"     value={compTo}   onChange={setCompTo} />
              <ComposeField label="Cc"    value={compCc}   onChange={setCompCc} />
              <ComposeField label="Objet" value={compSubj} onChange={setCompSubj} />
              <SigSelector store={store} sigId={sigId} setSigId={setSigId} onManage={() => setSigManager(true)} />
            </div>
            <RichToolbar editorRef={compEditorRef} />
            <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg2)', display: 'flex', flexDirection: 'column' }}>
              <RichEditor editorRef={compEditorRef}
                placeholder={mode === 'forward' ? 'Ajoutez un commentaire…' : 'Écrivez votre réponse…'}
                onInput={(_, hasContent) => setEditorHasContent(hasContent)}
                minHeight={120} />
              {quotedHtml && (
                <div style={{ borderTop: '1px dashed var(--border)', margin: '0 12px', padding: '10px 0' }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, userSelect: 'none' }}>— Message original —</div>
                  <div style={{ pointerEvents: 'none', opacity: 0.85 }} dangerouslySetInnerHTML={{ __html: quotedHtml }} />
                </div>
              )}
            </div>
            <div style={{ padding: '6px 14px 8px', borderTop: '1px solid var(--border)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {attachFiles.length > 0 && (
                <div style={{ paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
                  <AttachmentPicker files={attachFiles} onChange={setAttachFiles} />
                </div>
              )}
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
                <Btn onClick={handleSend} disabled={sending || !compTo || !editorHasContent} accent>{sending ? 'Envoi…' : 'Envoyer'}</Btn>
                <Btn onClick={handleCorrect} disabled={correcting || !editorHasContent}>{correcting ? 'Correction…' : '✦ Corriger'}</Btn>
                <Btn onClick={handleAI} disabled={aiLoading}>{aiLoading ? '…' : '↺ Réponse IA'}</Btn>
                <Btn onClick={() => setMode(null)} ghost>Annuler</Btn>
                <div style={{ marginLeft: 'auto' }}>
                  <AttachmentPicker files={attachFiles} onChange={setAttachFiles} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {sigManager && <SigManagerModal onClose={closeSigManager} />}
    </div>
  );
}

// ── Utilitaires UI ─────────────────────────────────────────────────────────────
function Placeholder({ children }) {
  return <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 13 }}>{children}</div>;
}
function Btn({ children, onClick, disabled, accent, ghost }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ padding: '6px 13px', borderRadius: 6, border: ghost ? '1px solid var(--border)' : 'none', background: accent ? 'var(--accent)' : ghost ? 'transparent' : 'var(--bg3)', color: accent ? '#fff' : ghost ? 'var(--text3)' : 'var(--text2)', fontSize: 12, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 }}>
      {children}
    </button>
  );
}
function btnS(bg, color) {
  return { padding: '7px 18px', borderRadius: 8, border: 'none', background: bg, color, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
}
const sectionLabel = { fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', margin: '6px 0 8px' };

// ── Page principale ────────────────────────────────────────────────────────────
export default function Emails() {
  const isAdmin = getJwtRole() === 'admin';
  const [mailbox, setMailbox]           = useState(() => isAdmin ? 'personal' : 'planif');
  const [view, setView]                 = useState('inbox');
  const [emails, setEmails]             = useState([]);
  const [sentEmails, setSentEmails]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [sentLoading, setSentLoading]   = useState(false);
  const [selectedUid, setSelectedUid]   = useState(null);
  const [filterSecteur, setFilterSecteur]     = useState('all');
  const [filterUnread, setFilterUnread]       = useState(false);
  const [filterRecipient, setFilterRecipient] = useState('all');
  const [search, setSearch]             = useState('');
  const [syncing, setSyncing]           = useState(false);
  const [compose, setCompose]           = useState(false);
  const [sigManager, setSigManager]     = useState(false);

  const [listWidth, setListWidth] = useState(() => parseInt(localStorage.getItem('email_list_width') || '300', 10));
  const dragging  = useRef(false);
  const dragStart = useRef({ x: 0, w: 0 });

  const mbBase    = mailbox === 'personal' ? '/admin/emails/personal' : '/admin/emails';
  const selfEmail = mailbox === 'personal' ? PERSONAL_EMAIL : PLANIF_EMAIL;

  const onDragDown = (e) => {
    dragging.current  = true;
    dragStart.current = { x: e.clientX, w: listWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };
  useEffect(() => {
    const onMove = (e) => { if (!dragging.current) return; setListWidth(Math.max(200, Math.min(560, dragStart.current.w + e.clientX - dragStart.current.x))); };
    const onUp   = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setListWidth(prev => { localStorage.setItem('email_list_width', String(prev)); return prev; });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  const loadInbox = useCallback(async (force = false) => {
    try {
      const r = force ? await api.post(`${mbBase}/refresh`) : await api.get(mbBase);
      setEmails(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setSyncing(false); }
  }, [mbBase]);

  const loadSent = useCallback(async (force = false) => {
    setSentLoading(true);
    try {
      const r = force ? await api.post(`${mbBase}/sent/refresh`) : await api.get(`${mbBase}/sent`);
      setSentEmails(r.data);
    } catch (e) { console.error(e); }
    finally { setSentLoading(false); setSyncing(false); }
  }, [mbBase]);

  useEffect(() => { loadInbox(); }, [loadInbox]);

  const handleMailboxChange = (mb) => {
    if (mb === mailbox) return;
    setMailbox(mb); setEmails([]); setSentEmails([]); setSelectedUid(null);
    setView('inbox'); setSearch(''); setLoading(true);
    setFilterSecteur('all'); setFilterUnread(false); setFilterRecipient('all');
  };

  const handleViewChange = (v) => {
    setView(v); setSelectedUid(null); setSearch('');
    if (v === 'sent' && sentEmails.length === 0) loadSent();
  };

  const handleRefresh = () => {
    setSyncing(true);
    if (view === 'inbox') loadInbox(true); else loadSent(true);
  };

  const handleSecteur = async (uid, secteur) => {
    if (mailbox !== 'planif') return;
    await api.post(`/admin/emails/${uid}/secteur`, { secteur }).catch(() => {});
    setEmails(prev => prev.map(e => e.uid === uid ? { ...e, secteur } : e));
  };

  const handleMarkRead = useCallback(async (uid, read) => {
    setEmails(prev => prev.map(e => e.uid === uid ? { ...e, read } : e));
    try {
      await api.post(`${mbBase}/${uid}/read`, { read });
    } catch {
      setEmails(prev => prev.map(e => e.uid === uid ? { ...e, read: !read } : e));
    }
  }, [mbBase]);

  const activeEmails  = view === 'inbox' ? emails : sentEmails;
  const isLoading     = view === 'inbox' ? loading : sentLoading;
  const showSecteur   = mailbox === 'planif';
  const selectedEmailRead = view === 'inbox'
    ? (emails.find(e => e.uid === selectedUid)?.read ?? true)
    : true;
  const personalLower = PERSONAL_EMAIL.toLowerCase();
  const unreadCount   = emails.filter(e => !e.read).length;
  const counts = { A: emails.filter(e => e.secteur === 'A').length, B: emails.filter(e => e.secteur === 'B').length };

  const filtered = activeEmails.filter(e => {
    if (view === 'inbox' && filterUnread && e.read) return false;
    if (view === 'inbox' && showSecteur && filterSecteur !== 'all' && e.secteur !== filterSecteur) return false;
    if (view === 'inbox' && mailbox === 'personal' && filterRecipient !== 'all') {
      if (filterRecipient === 'to' && !e.toList?.some(a => a.includes(personalLower))) return false;
      if (filterRecipient === 'cc' && !e.ccList?.some(a => a.includes(personalLower))) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (view === 'sent') return e.subject?.toLowerCase().includes(q) || e.to?.toLowerCase().includes(q);
      return e.subject?.toLowerCase().includes(q) || e.from?.toLowerCase().includes(q) || e.refCommande?.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '10px 18px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Boîte mail</h1>
            {isAdmin && (
              <div style={{ display: 'flex', gap: 3, background: 'var(--bg2)', borderRadius: 8, padding: 3, border: '1px solid var(--border)' }}>
                {[['planif', '📬 Planif'], ['personal', '👤 Perso']].map(([key, label]) => (
                  <button key={key} onClick={() => handleMailboxChange(key)}
                    style={{ padding: '3px 10px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer', background: mailbox === key ? 'var(--accent)' : 'transparent', color: mailbox === key ? '#fff' : 'var(--text3)', transition: 'all .15s' }}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{selfEmail}</span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setCompose(true)} style={{ padding: '5px 13px', borderRadius: 6, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>✉ Nouveau</button>
            <button onClick={() => setSigManager(true)} style={{ padding: '5px 11px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', fontSize: 12, cursor: 'pointer' }}>✏ Signatures</button>
            <button onClick={handleRefresh} disabled={syncing}
              style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', fontSize: 12, cursor: syncing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ display: 'inline-block', animation: syncing ? 'spin 1s linear infinite' : 'none' }}>↺</span>
              {syncing ? 'Actualisation…' : 'Actualiser'}
            </button>
          </div>
        </div>

        {/* Onglets */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 8, borderBottom: '2px solid var(--border)' }}>
          {[['inbox', '📥 Reçus', unreadCount], ['sent', '📤 Envoyés', 0]].map(([v, label, badge]) => (
            <button key={v} onClick={() => handleViewChange(v)}
              style={{ padding: '6px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', borderBottom: `2px solid ${view === v ? 'var(--accent)' : 'transparent'}`, marginBottom: -2, background: 'transparent', color: view === v ? 'var(--accent)' : 'var(--text3)', display: 'flex', alignItems: 'center', gap: 6, transition: 'color .15s' }}>
              {label}
              {badge > 0 && <span style={{ background: '#3b82f6', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>{badge}</span>}
            </button>
          ))}
        </div>

        {/* Filtres boîte perso */}
        {view === 'inbox' && mailbox === 'personal' && (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setFilterUnread(v => !v)}
              style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10, cursor: 'pointer', border: `1.5px solid ${filterUnread ? '#3b82f6' : 'var(--border)'}`, background: filterUnread ? 'rgba(59,130,246,.1)' : 'var(--bg3)', color: filterUnread ? '#3b82f6' : 'var(--text3)' }}>
              Non lus{filterUnread && unreadCount > 0 ? ` (${unreadCount})` : ''}
            </button>
            <span style={{ color: 'var(--border)' }}>|</span>
            {[['all', 'Tous'], ['to', 'Destinataire direct'], ['cc', 'En copie']].map(([v, lbl]) => (
              <button key={v} onClick={() => setFilterRecipient(v)}
                style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10, cursor: 'pointer', border: `1.5px solid ${filterRecipient === v ? 'var(--accent)' : 'var(--border)'}`, background: filterRecipient === v ? 'color-mix(in srgb,var(--accent) 10%,var(--bg))' : 'var(--bg3)', color: filterRecipient === v ? 'var(--accent)' : 'var(--text3)' }}>
                {lbl}
              </button>
            ))}
          </div>
        )}

        {/* Filtres boîte planif */}
        {view === 'inbox' && showSecteur && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setFilterUnread(v => !v)}
              style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10, cursor: 'pointer', border: `1.5px solid ${filterUnread ? '#3b82f6' : 'var(--border)'}`, background: filterUnread ? 'rgba(59,130,246,.1)' : 'var(--bg3)', color: filterUnread ? '#3b82f6' : 'var(--text3)' }}>
              Non lus{filterUnread && unreadCount > 0 ? ` (${unreadCount})` : ''}
            </button>
            <span style={{ color: 'var(--border)' }}>|</span>
            {[['all', 'Tous'], ['A', `Sec. A${counts.A ? ` (${counts.A})` : ''}`], ['B', `Sec. B${counts.B ? ` (${counts.B})` : ''}`]].map(([v, lbl]) => (
              <button key={v} onClick={() => setFilterSecteur(v)}
                style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10, cursor: 'pointer', border: `1.5px solid ${filterSecteur === v ? (SECTEUR_COLOR[v] || '#185FA5') : 'var(--border)'}`, background: filterSecteur === v ? ((SECTEUR_COLOR[v] || '#185FA5') + '18') : 'var(--bg3)', color: filterSecteur === v ? (SECTEUR_COLOR[v] || '#185FA5') : 'var(--text3)' }}>
                {lbl}
              </button>
            ))}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text3)' }}>
              <span style={{ width: 8, height: 8, background: SECTEUR_COLOR.A, borderRadius: 2, display: 'inline-block' }} /> A
              <span style={{ width: 8, height: 8, background: SECTEUR_COLOR.B, borderRadius: 2, display: 'inline-block', marginLeft: 6 }} /> B
              <span style={{ marginLeft: 4 }}>— survoler pour assigner</span>
            </div>
          </div>
        )}
      </div>

      {/* Layout 2 colonnes */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', borderTop: '1px solid var(--border)' }}>
        <div style={{ width: listWidth, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '7px 10px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={view === 'sent' ? 'Rechercher (destinataire, objet)…' : 'Rechercher…'}
              style={{ width: '100%', padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 12, boxSizing: 'border-box', outline: 'none' }} />
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {isLoading
              ? <div style={{ padding: 20, color: 'var(--text3)', fontSize: 12, textAlign: 'center' }}>Chargement…</div>
              : filtered.length === 0
                ? <div style={{ padding: 20, color: 'var(--text3)', fontSize: 12, textAlign: 'center' }}>
                    {view === 'sent' ? 'Aucun message envoyé' : 'Aucun email'}
                  </div>
                : filtered.map(email => {
                    const isCC = mailbox === 'personal' && view === 'inbox'
                      && !!email.ccList?.some(a => a.includes(personalLower))
                      && !email.toList?.some(a => a.includes(personalLower));
                    return (
                      <EmailItem key={email.uid} email={email} selected={selectedUid === email.uid}
                        onClick={() => setSelectedUid(email.uid)}
                        onSecteur={handleSecteur} sentView={view === 'sent'}
                        showSecteur={showSecteur} isCC={isCC} onMarkRead={handleMarkRead} />
                    );
                  })
            }
          </div>
          <div style={{ padding: '5px 12px', borderTop: '1px solid var(--border)', fontSize: 10, color: 'var(--text3)', flexShrink: 0 }}>
            {filtered.length} message{filtered.length > 1 ? 's' : ''}
          </div>
        </div>

        <div onMouseDown={onDragDown} title="Glisser pour redimensionner"
          style={{ width: 5, flexShrink: 0, cursor: 'col-resize', background: 'var(--border)', transition: 'background .15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--accent)'}
          onMouseLeave={e => { if (!dragging.current) e.currentTarget.style.background = 'var(--border)'; }} />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          {selectedUid
            ? <EmailDetail key={`${mailbox}-${view}-${selectedUid}`} uid={selectedUid} sentView={view === 'sent'} mbBase={mbBase} selfEmail={selfEmail} emailRead={selectedEmailRead} onMarkRead={handleMarkRead} />
            : <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', gap: 10 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 48, height: 48, opacity: 0.3 }}>
                  <path d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                <span style={{ fontSize: 13 }}>Sélectionnez un message</span>
                {view === 'inbox' && <button onClick={() => setCompose(true)} style={{ marginTop: 8, padding: '6px 16px', borderRadius: 7, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✉ Nouveau message</button>}
              </div>
          }
        </div>
      </div>

      {compose    && <ComposeModal onClose={() => setCompose(false)} mbBase={mbBase} />}
      {sigManager && <SigManagerModal onClose={() => setSigManager(false)} />}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
